import React, { useState } from 'react';
import { app } from './firebase';
import { getFirestore, collection, addDoc, updateDoc, doc, deleteDoc, query, where, getDocs } from 'firebase/firestore';

const db = getFirestore(app);

export default function InvoicesView({ activeContracts, invoices, loading, logAction, isReadOnly }) {
  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(today.toLocaleString('default', { month: 'long' }));
  const [viewArchived, setViewArchived] = useState(false); 
  
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const years = [2024, 2025, 2026, 2027];

  // Helper: Calculate Due Date
  const calculateDueDate = (inv, type) => {
      const monthIdx = months.indexOf(inv.month);
      const invDate = new Date(inv.year, monthIdx + 1, 0); // End of month
      const terms = type === 'client' ? (inv.clientTerms || 30) : (inv.consultantTerms || 30);
      invDate.setDate(invDate.getDate() + parseInt(terms));
      return invDate.toISOString().split('T')[0];
  };

  // --- FINANCIAL CALCULATOR ---
  const calculateFinancials = (inv) => {
      let revenue = 0; // Pre-VAT
      let cost = 0;
      let vatAmount = 0;

      // 1. Use values saved in DB
      if (inv.calculatedAmount !== undefined) {
          revenue = parseFloat(inv.calculatedAmount);
          cost = parseFloat(inv.calculatedCost || 0);
      } 
      // 2. Fallback Calculation
      else if (inv.totalHours !== undefined && inv.totalHours !== null) {
          const hourlySell = (parseFloat(inv.dayRate) || 0) / 8;
          const hourlyBuy = (parseFloat(inv.costRate) || 0) / 8;
          revenue = hourlySell * parseFloat(inv.totalHours);
          cost = hourlyBuy * parseFloat(inv.totalHours);
      }

      const invVatRate = inv.vatRate !== undefined ? parseFloat(inv.vatRate) : 21;
      vatAmount = revenue * (invVatRate / 100);
      const totalAmount = revenue + vatAmount;

      return { 
          preVat: revenue, 
          vat: vatAmount, 
          total: totalAmount, 
          cost, 
          margin: revenue - cost,
          marginPercent: revenue > 0 ? ((revenue - cost) / revenue) * 100 : 0
      };
  };

  // --- SMART GENERATE HANDLER (STRICT MODE) ---
  const handleGenerateInvoices = async () => {
      if(isReadOnly) return;
      
      const confirm = window.confirm(`Generate invoices for ${selectedMonth} ${selectedYear}? \n\nOnly contracts with valid timesheets will be processed.`);
      if(!confirm) return;

      let createdCount = 0;
      let skippedCount = 0;
      const monthIdx = months.indexOf(selectedMonth);
      const periodStart = new Date(selectedYear, monthIdx, 1);
      const periodEnd = new Date(selectedYear, monthIdx + 1, 0);

      for (const contract of activeContracts) {
          const cStart = new Date(contract.startDate);
          const cEnd = new Date(contract.endDate);
          
          // Check if contract is active in this period
          if (cStart <= periodEnd && cEnd >= periodStart) {
              const exists = invoices.find(i => 
                  i.contractId === contract.id && 
                  i.month === selectedMonth && 
                  i.year === selectedYear
              );

              if (!exists) {
                  // Query timesheets for this consultant/month
                  const q = query(
                      collection(db, "timesheets"), 
                      where("consultantId", "==", contract.consultantId),
                      where("month", "==", selectedMonth),
                      where("year", "==", selectedYear)
                  );
                  
                  const tSnap = await getDocs(q);
                  
                  // STRICT RULE: Only generate if timesheet exists
                  if (!tSnap.empty) {
                      const tData = tSnap.docs[0].data();
                      const finalHours = parseFloat(tData.totalHours) || 0;
                          
                      // Calculate exact amounts (Rate / 8 * Hours)
                      const hourlySell = (parseFloat(contract.dayRate) || 0) / 8;
                      const hourlyBuy = (parseFloat(contract.costRate) || 0) / 8;
                      
                      const finalAmount = hourlySell * finalHours;
                      const finalCost = hourlyBuy * finalHours;
                      const source = 'Generated from Timesheet Data';

                      // Create Invoice
                      await addDoc(collection(db, "invoices"), {
                          contractId: contract.id,
                          consultantName: contract.consultantName,
                          clientName: contract.client,
                          month: selectedMonth,
                          year: selectedYear,
                          dayRate: contract.dayRate || 0,
                          costRate: contract.costRate || 0,
                          
                          // Save calculated values
                          totalHours: finalHours,
                          calculatedAmount: finalAmount,
                          calculatedCost: finalCost,
                          vatRate: 21, // Default to 21, can be changed per invoice
                          source: source,

                          consultantStatus: 'Pending',
                          consultantTerms: 30,
                          earlyPayDiscount: 0,
                          earlyPayDeadline: 0,
                          clientStatus: 'Sent',
                          clientTerms: 30,
                          createdAt: new Date().toISOString(),
                          archived: false
                      });
                      createdCount++;
                  } else {
                      skippedCount++; // No timesheet found, skipping
                  }
              }
          }
      }
      
      const resultMsg = `Process Complete.\n\nGenerated: ${createdCount}\nSkipped (No Timesheet): ${skippedCount}`;
      logAction("Generate Invoices", `Generated ${createdCount} invoices. Skipped ${skippedCount}.`);
      alert(resultMsg);
  };

  const updateInvoice = async (id, field, value) => {
      if(isReadOnly) return;
      
      const inv = invoices.find(i => i.id === id);
      if (!inv) return;

      const updates = { [field]: value };

      // Auto-archive check
      let newClientStatus = inv.clientStatus;
      let newConsultantStatus = inv.consultantStatus;

      if (field === 'clientStatus') newClientStatus = value;
      if (field === 'consultantStatus') newConsultantStatus = value;

      if (newClientStatus === 'Paid' && newConsultantStatus === 'Paid') {
          updates.archived = true;
      }

      await updateDoc(doc(db, "invoices", id), updates);
  };

  const handleUnarchive = async (id) => {
      if(isReadOnly) return;
      if(window.confirm("Restore this invoice to Active view?")) {
          await updateDoc(doc(db, "invoices", id), { archived: false });
      }
  };

  const currentInvoices = invoices.filter(i => 
      i.month === selectedMonth && 
      i.year === selectedYear &&
      (viewArchived ? i.archived === true : i.archived !== true)
  );

  return (
    <div className="container-fluid p-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-main">Invoices</h2>
          <p className="text-muted-theme">Manage billing and per-invoice VAT.</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <div className="btn-group me-2">
             <button className={`btn ${!viewArchived ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewArchived(false)}>Active</button>
             <button className={`btn ${viewArchived ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setViewArchived(true)}>Archived</button>
          </div>

          <select className="form-select" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}>
            {months.map(m => <option key={m}>{m}</option>)}
          </select>
          <select className="form-select" value={selectedYear} onChange={e => setSelectedYear(e.target.value)}>
            {years.map(y => <option key={y}>{y}</option>)}
          </select>
          {!isReadOnly && !viewArchived && (
              <button className="btn btn-primary" onClick={handleGenerateInvoices}>
                <i className="bi bi-magic me-2"></i> Generate
              </button>
          )}
        </div>
      </div>

      {loading ? <div className="text-main">Loading Invoices...</div> : (
        <div className="table-responsive bg-card border border-theme rounded shadow-sm">
            <table className="table table-hover mb-0 text-main align-middle">
                <thead className="bg-input">
                    <tr>
                        <th className="bg-card text-muted-theme border-theme">Contract / Consultant</th>
                        <th className="bg-card text-muted-theme border-theme text-center">Rates (Day)</th>
                        <th className="bg-card text-muted-theme border-theme text-end">Pre-VAT</th>
                        <th className="bg-card text-muted-theme border-theme text-end">VAT %</th>
                        <th className="bg-card text-muted-theme border-theme text-end">Total (Inc. VAT)</th>
                        <th className="bg-card text-muted-theme border-theme text-end">Margin</th>
                        <th className="bg-card text-muted-theme border-theme text-center">Status</th>
                        <th className="bg-card text-muted-theme border-theme"></th>
                    </tr>
                </thead>
                <tbody>
                    {currentInvoices.map(inv => {
                        const fin = calculateFinancials(inv);
                        const hasExactData = inv.totalHours !== undefined || inv.calculatedAmount !== undefined;

                        return (
                            <tr key={inv.id} className={inv.archived ? "opacity-75" : ""}>
                                {/* CONTRACT INFO */}
                                <td className="bg-card border-theme">
                                    <div className="fw-bold">{inv.clientName}</div>
                                    <div className="small text-muted-theme">{inv.consultantName}</div>
                                    {hasExactData ? (
                                        <span className="badge bg-info text-dark mt-1" title="Based on actual timesheet hours">
                                            {inv.totalHours ? `${inv.totalHours} hrs` : 'Actuals'}
                                        </span>
                                    ) : (
                                        <span className="badge bg-secondary bg-opacity-50 text-muted mt-1" title="No Data">No Data</span>
                                    )}
                                </td>

                                {/* RATES */}
                                <td className="bg-card border-theme text-center small">
                                    <div className="text-success">Sell: €{inv.dayRate}</div>
                                    <div className="text-danger">Buy: €{inv.costRate || 0}</div>
                                </td>

                                {/* PRE-VAT */}
                                <td className="bg-card border-theme text-end fw-bold">
                                    €{fin.preVat.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>

                                {/* VAT % (Editable) */}
                                <td className="bg-card border-theme text-end" style={{width: '120px'}}>
                                    <div className="input-group input-group-sm">
                                        <input 
                                            type="number" 
                                            className="form-control bg-input border-theme text-main text-end" 
                                            value={inv.vatRate !== undefined ? inv.vatRate : 21} 
                                            onChange={(e) => updateInvoice(inv.id, 'vatRate', e.target.value)}
                                            disabled={isReadOnly || (viewArchived && inv.archived)}
                                        />
                                        <span className="input-group-text bg-input border-theme text-muted-theme">%</span>
                                    </div>
                                    <div className="small text-muted-theme mt-1">€{fin.vat.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                </td>

                                {/* TOTAL */}
                                <td className="bg-card border-theme text-end fw-bold text-primary">
                                    €{fin.total.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>

                                {/* MARGIN */}
                                <td className="bg-card border-theme text-end">
                                    <div className="text-success fw-bold">€{fin.margin.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
                                    <div className="small text-muted-theme">{fin.marginPercent.toFixed(1)}%</div>
                                </td>

                                {/* STATUS */}
                                <td className="bg-card border-theme text-center" style={{minWidth: '150px'}}>
                                    <select 
                                        className={`form-select form-select-sm mb-1 ${inv.clientStatus === 'Paid' ? 'border-success text-success' : ''}`}
                                        value={inv.clientStatus} 
                                        onChange={(e) => updateInvoice(inv.id, 'clientStatus', e.target.value)}
                                        disabled={isReadOnly || (viewArchived && inv.archived)}
                                    >
                                        <option>Draft</option>
                                        <option>Sent</option>
                                        <option>Overdue</option>
                                        <option>Paid</option>
                                    </select>
                                    <div className="small text-muted-theme">Consultant: {inv.consultantStatus}</div>
                                </td>

                                <td className="bg-card border-theme">
                                    {!isReadOnly && (
                                        <>
                                            {inv.archived ? (
                                                <button className="btn btn-sm btn-link text-warning" onClick={() => handleUnarchive(inv.id)} title="Restore">
                                                    <i className="bi bi-arrow-counterclockwise"></i>
                                                </button>
                                            ) : (
                                                <button className="btn btn-sm btn-link text-danger" onClick={() => {if(window.confirm('Delete invoice?')) deleteDoc(doc(db, "invoices", inv.id))}}>
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {currentInvoices.length === 0 && (
                        <tr>
                            <td colSpan="8" className="text-center py-5 text-muted-theme">
                                {viewArchived 
                                    ? "No archived invoices for this period." 
                                    : "No active invoices. Click 'Generate' to start."
                                }
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
      )}
    </div>
  );
}